// Shared state for the field photo upload queue (see utils/fieldUploadQueue.ts).

export function useFieldQueue() {
  const { fieldFetch, session } = useField()
  const owner = computed(() => {
    const account = session.value
    if (!account?.allowed_departments.length) return null
    return account.worker_id
      ? `person:${account.worker_id}`
      : `crew:${account.organization}:${account.crew}:${account.phone_number}`
  })
  const belongs = (item: QueuedPhoto) =>
    Boolean(
      owner.value &&
      (item.ownerKey
        ? item.ownerKey === owner.value
        : !session.value?.worker_id)
    )
  const stored = useState<QueuedPhoto[]>('field-queue-items', () => [])
  const items = computed(() => stored.value.filter(belongs))
  const uploading = useState<boolean>('field-queue-uploading', () => false)
  const lastUploadAt = useState<number>('field-queue-last-upload', () => 0)

  const pending = computed(() =>
    items.value.filter((item) => !item.failed && item.target)
  )
  // Photos taken before choosing the job.
  const unassigned = computed(() => items.value.filter((item) => !item.target))
  const failed = computed(() => items.value.filter((item) => item.failed))

  const refresh = async () => {
    try {
      stored.value = await queuedPhotos()
    } catch {
      stored.value = []
    }
  }

  const process = async () => {
    if (!owner.value || uploading.value || !navigator.onLine) return
    uploading.value = true
    try {
      const uploaded = await uploadQueuedPhotos(
        (body) => fieldFetch('/photos/', { method: 'POST', body }),
        refresh,
        belongs
      )
      if (uploaded) lastUploadAt.value = Date.now()
    } finally {
      uploading.value = false
      await refresh()
    }
  }

  const add = async (
    files: File[],
    target: QueueTarget | null,
    position: FieldPosition | null
  ) => {
    const ownerKey = owner.value
    const department = session.value?.department ?? null
    if (!ownerKey) throw new Error(ft('Sign in again to save photos.'))
    for (const file of files) {
      const blob = await compressPhoto(file)
      await saveQueuedPhoto({
        client_uuid: newClientUuid(),
        target,
        ownerKey,
        department,
        blob,
        latitude: position?.latitude ?? null,
        longitude: position?.longitude ?? null,
        taken_at: new Date(file.lastModified || Date.now()).toISOString(),
        created_at: Date.now(),
        attempts: 0,
        error: null,
        failed: false,
      })
    }
    await refresh()
    process()
  }

  /** Send the photos taken before choosing a job to that job. */
  const assign = async (target: QueueTarget) => {
    for (const item of unassigned.value) {
      await saveQueuedPhoto({ ...item, target })
    }
    await refresh()
    process()
  }

  const retry = async (item: QueuedPhoto) => {
    await saveQueuedPhoto({ ...item, failed: false, error: null })
    await refresh()
    process()
  }

  const discard = async (item: QueuedPhoto) => {
    await removeQueuedPhoto(item.client_uuid)
    await refresh()
  }

  /** Call once from the field layout: keeps the queue moving while the page is open. */
  const start = () => {
    refresh().then(process)
    const onWake = () => {
      if (document.visibilityState === 'visible') process()
    }
    window.addEventListener('online', process)
    document.addEventListener('visibilitychange', onWake)
    const timer = window.setInterval(process, 30000)
    return () => {
      window.removeEventListener('online', process)
      document.removeEventListener('visibilitychange', onWake)
      window.clearInterval(timer)
    }
  }

  return {
    add,
    assign,
    unassigned,
    discard,
    failed,
    items,
    lastUploadAt,
    pending,
    process,
    refresh,
    retry,
    start,
    uploading,
  }
}
